/**
 * Pages « vitrine » : celles qu'un visiteur sans compte parcourt avant de
 * s'inscrire, en arrivant depuis la page d'accueil ou depuis Google.
 *
 * Sur ces pages, un visiteur non connecté garde la barre de navigation de la
 * page d'accueil (mêmes liens, bouton d'inscription toujours visible) au lieu
 * de basculer dans l'interface de l'application. Il ne perd pas son fil au
 * moment précis où on lui demande de payer. Dès qu'une session existe,
 * l'utilisateur retrouve sa barre latérale habituelle : quelqu'un qui vient
 * changer d'offre depuis son espace doit rester dans son espace.
 *
 * Deux règles à respecter en ajoutant une entrée :
 *
 * 1. Une page qui affiche déjà son propre en-tête (/press, les pages
 *    d'accélérateur) n'a rien à faire ici : elle en afficherait deux.
 * 2. Une page de consultation de contenu (lecteur de session, épisode de
 *    série, atelier) n'est pas une page vitrine : elle garde l'interface de
 *    l'application. D'où les motifs à un seul segment ci-dessous, qui
 *    retiennent la fiche d'une formation mais pas ses sessions.
 *
 * Les chemins sont comparés sans la locale : usePathname de @/i18n/navigation
 * renvoie "/pricing" aussi bien sur /fr/tarifs que sur /en/pricing. Les motifs
 * fonctionnent que next-intl renvoie le segment réel ("/course/formation-html")
 * ou le gabarit de la route ("/course/[slug]").
 */
const EXACT_PATHS = [
    "/pricing",
    "/courses",
    "/accelerators",
    "/mentoring",
    "/workshops",
    "/articles",
    "/community",
    // Le chemin de leçons. Le lecteur (/reps/[lessonSlug]) n'est pas ici : il
    // s'affiche en plein écran par-dessus la page, le chrome ne le concerne pas.
    "/reps",
    // La page de présentation des offres équipes. Son espace de gestion
    // (/teams/dashboard) et son tunnel de démonstration (/teams/demo, qui
    // affiche déjà son propre logo) gardent leur mise en page.
    "/teams",
    // L'index du glossaire : une liste de fiches consultables sans compte,
    // au même titre que /articles.
    "/glossaries",
    // Pages de recrutement (formateurs, mentors) et le changelog : des pages
    // vitrine à part entière, sans lien avec l'espace membre.
    "/join-instructors",
    "/join-mentors",
    "/changelog",
    // La page de présentation de Clap : une page de vente, indexée, qu'on
    // ouvre depuis Google ou un lien, sans compte.
    "/clap",
    // La rubrique des outils : gratuits et sans compte, et le menu de la page
    // d'accueil y mène déjà. Y arriver dans l'interface membre, barre latérale
    // comprise, faisait changer de site au milieu d'un clic.
    "/tools",
    // Les pages légales : on y arrive du pied de page, d'un lien de facture
    // ou d'un outil intégré ailleurs, presque toujours sans compte. Un visiteur
    // n'a rien à faire d'une barre latérale de membre pour lire des CGV.
    "/terms-of-sale",
    "/terms-of-use",
    "/tools-terms",
    "/privacy",
    "/legal-mentions",
    // L'aide et la confidentialité de Folio : les deux adresses données à
    // l'App Store, ouvertes sans compte depuis la fiche de l'app.
    "/folio",
    "/folio/privacy",
    // Là où mènent les liens des e-mails d'alerte des outils : confirmer ou
    // arrêter une alerte, presque toujours sans être connecté.
    "/tools-alert",
];

// Un seul segment après le préfixe : la fiche, jamais le contenu qu'elle ouvre.
const SINGLE_SEGMENT_PATTERNS = [
    /^\/course\/[^/]+$/,
    /^\/series\/[^/]+$/,
    /^\/workshops\/[^/]+$/,
    /^\/mentoring\/[^/]+$/,
    /^\/r\/[^/]+$/,
    /^\/community\/discussions\/[^/]+$/,
    /^\/certifications\/[^/]+$/,
    // Profil public. La route canonique est /u/[pseudo], mais l'adresse
    // affichée est /@pseudo : les deux formes peuvent remonter ici.
    /^\/u\/[^/]+$/,
    /^\/@[^/]+$/,
    // La page d'un glossaire précis (liste de ses termes).
    /^\/glossaries\/[^/]+$/,
    // La page d'un outil : c'est elle que Google envoie, bien plus que l'index.
    /^\/tools\/[^/]+$/,
];

// Contenu de lecture pure, comme /r/[slug], mais avec un segment de plus dans
// l'URL : la fiche d'un terme de glossaire. Contrairement à une session de
// formation, il n'y a ni progression ni inscription à suivre : la règle
// "pas de contenu" au-dessus ne s'applique pas ici.
const GLOSSARY_TERM_PATTERN = /^\/glossaries\/[^/]+\/[^/]+$/;

// Les pages de sujet des articles (/articles/entreprendre, un sous-thème, la
// suite d'une liste) : la même liste que /articles, filtrée. Quelle que soit
// leur profondeur, elles gardent son habillage, sinon cliquer sur un sujet
// ferait passer de la barre du haut au menu latéral.
const ARTICLE_TOPIC_PATTERN = /^\/articles\/.+$/;

export function isPublicMarketingPath(pathname) {
    if (!pathname) return false;

    if (EXACT_PATHS.includes(pathname)) return true;

    if (ARTICLE_TOPIC_PATTERN.test(pathname)) return true;

    if (GLOSSARY_TERM_PATTERN.test(pathname)) return true;

    return SINGLE_SEGMENT_PATTERNS.some((pattern) => pattern.test(pathname));
}
